/**
 * Tutor information-session detail tab.
 * Uses the hydrated display slice (no extra fetch) and can mark / cancel participation.
 */
import * as React from 'react';
import { Calendar, CheckCircle2, Clock, Info } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { Button } from '@/components/forms/Button';
import { useMarkInPersonTutorInformationMeetingParticipation } from '@/hooks/useUsers';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  formatTutorShortDate,
  type TutorContentDisplay,
} from '@/components/tutors/inperson/tutor-table-utils';

/** Props for {@link TutorInfoSessionTab} — hydrated display slice (no extra fetch). */
export const TutorInfoSessionTab: React.FC<{
  display: TutorContentDisplay;
}> = ({ display }) => {
  const { user: authUser } = useAuthStore();
  const companyId = authUser?.companyId ?? '';
  const { userId, infoSession } = display;
  const markInformationMeetingMutation = useMarkInPersonTutorInformationMeetingParticipation();

  /** Mark or cancel information-meeting participation. */
  const handleMarkParticipation = (participated: boolean) => {
    markInformationMeetingMutation.mutate(
      {
        userId,
        companyId,
        params: {
          participated,
          participatedAt: participated ? new Date().toISOString() : undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success(participated ? 'Participation marquée' : 'Participation annulée');
        },
        onError: (error) => {
          toast.error(getErrorMessage(error));
        },
      },
    );
  };

  return (
    <DetailsCard icon={Info} title="Réunion d'information" colorName="color-3" colSpan={2}>
      <div className="space-y-3">
        <LabelBadge
          label={infoSession.hasParticipated ? 'Participé' : 'Non participé'}
          icon={infoSession.hasParticipated ? CheckCircle2 : Clock}
          variant="outlined"
          color={
            infoSession.hasParticipated
              ? 'rgb(var(--status-success))'
              : 'rgb(var(--status-pending))'
          }
          size="sm"
        />
        {infoSession.participatedAt && (
          <DetailField
            icon={Calendar}
            label="Date de participation"
            value={formatTutorShortDate(infoSession.participatedAt)}
          />
        )}
        <div className="flex gap-2 pt-2 border-t">
          {!infoSession.hasParticipated ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleMarkParticipation(true)}
              disabled={markInformationMeetingMutation.isPending}
            >
              Marquer comme participé
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleMarkParticipation(false)}
              disabled={markInformationMeetingMutation.isPending}
            >
              Annuler la participation
            </Button>
          )}
        </div>
      </div>
    </DetailsCard>
  );
};
